import React, { useState, useReducer, useEffect, useMemo } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Table,
  message,
  Row,
  Col,
  Select,
  DatePicker,
  Radio,
  Popconfirm,
  Tooltip,
} from "antd";
import { v4 as uuidv4 } from "uuid";

import {
  FETCH_ALL_COLOR_API,
  FETCH_ALL_COUNTER_API,
  FETCH_ALL_DEPT_API,
} from "../../../Constants/Apis";
import { useFetch } from "../../../Utils/useFetch";
import { reducer } from "../../../Utils/CommonUtils";

import { createShippingWithStock } from "../../../Services/ShippingService";
import StockCreationForm from "../../elements/Forms/StockCreationForm/StockCreationForm";

// ------------------- helpers -------------------
const toISO = (d) => (d && d.format ? d.format("YYYY-MM-DD") : null);

// normalize numbers that might contain thousand separators like "1.000" or "1,000"
const toNumber = (x) =>
  Number(String(x ?? "").replace(/(?<=\d)[,.\s](?=\d{3}\b)/g, ""));

// explicit size parser supporting: "12-14", "12 14", "12..14", "12", "07"
// Accepts: "07", "12 15", "12-15", "1215", and "4244"
const parseSizes = (raw) => {
  if (raw == null) return [];
  const s = String(raw).trim();
  const m = s.match(/^(\d{1,2})\s*(?:-|\.{2}|\s)\s*(\d{1,2})$/);
  if (m) {
    const a = Number(m[1]),
      b = Number(m[2]);
    if (!Number.isInteger(a) || !Number.isInteger(b) || b < a) return [];
    return Array.from({ length: b - a + 1 }, (_, i) =>
      String(a + i).padStart(2, "0")
    );
  }
  if (/^\d{4}$/.test(s)) {
    const a = Number(s.slice(0, 2)),
      b = Number(s.slice(2, 4));
    if (!Number.isInteger(a) || !Number.isInteger(b) || b < a) return [];
    return Array.from({ length: b - a + 1 }, (_, i) =>
      String(a + i).padStart(2, "0")
    );
  }
  if (/^\d{1,2}$/.test(s)) return [String(Number(s)).padStart(2, "0")];
  return [];
};

const normalizeAssortList = (assort, sizesLen) => {
  const str = String(assort ?? "").trim();
  if (/^\d+$/.test(str) && str.length === sizesLen) return str.split(""); // "111" -> ["1","1","1"]
  return str.split(/[,\s]+/).filter(Boolean);
};

const ensureBarcode = (base, size2) => {
  if (!base || base.length < 2) {
    throw new Error("Invalid base barcode. It must be at least 2 characters.");
  }
  return `${base.slice(0, -2)}${size2}`;
};

// merge duplicate items based on stable key
const itemKey = (it) =>
  [it.article, it.color, it.size, it.barcode, it.price, it.harga_internal].join(
    "|"
  );

const upsertItem = (list, next) => {
  const map = new Map(list.map((it) => [itemKey(it), it]));
  const k = itemKey(next);
  if (map.has(k)) {
    const prev = map.get(k);
    map.set(k, {
      ...prev,
      quantity: toNumber(prev.quantity) + toNumber(next.quantity),
    });
  } else {
    map.set(k, next);
  }
  return Array.from(map.values());
};

const buildStockData = (values, items, consignmentType) => {
  const rows = [];

  for (const raw of items) {
    const sizes = parseSizes(raw.size);
    if (!sizes.length) continue;

    // compact assort handling
    let assortList;
    if (Array.isArray(raw.assort)) {
      assortList = raw.assort;
    } else {
      const str = String(raw.assort ?? "").trim();
      if (
        /^\d+$/.test(str) &&
        sizes.length > 1 &&
        str.length === sizes.length
      ) {
        assortList = str.split("");
      } else {
        assortList = str.split(/[,\s]+/).filter(Boolean);
      }
    }

    const perPack = Number(raw.inputQuantity ?? raw.quantity ?? 0);

    sizes.forEach((s2, i) => {
      const assortVal = Number(
        sizes.length > 1 ? assortList[i] ?? 1 : assortList[0] ?? 1
      );
      if (!Number.isInteger(assortVal) || assortVal <= 0) return;

      const quantity = assortVal * perPack;
      if (!Number.isFinite(quantity) || quantity <= 0) return;

      const barcode = `${String(raw.barcode).slice(0, -2)}${s2}`;

      rows.push({
        noSJ: values.noSJ,
        article: raw.article || "",
        brand: raw.brand || "", // Include brand for SKU generation
        color: raw.color || "",
        size: Number(s2),
        quantity,
        assort: String(assortVal),
        price: Number(raw.price) || 0,
        harga_internal: Number(raw.harga_internal) || 0,
        barcode,
        counterCode: values.destinationCode,
        currentQuantity: 0,
        status: "in delivery",
        consignmentType: consignmentType || "putus", // Include consignmentType
      });
    });
  }

  return rows;
};
// ------------------------------------------------

const ShippingCreationPage = ({ userData }) => {
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [items, setItems] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [isTempEditMode, setIsTempEditMode] = useState(false);
  const [isAddingNewItem, setIsAddingNewItem] = useState(false);
  const [consignmentType, setConsignmentType] = useState("putus");
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);

  const initialState = {
    searchKeyword: "",
    filteredInfo: {},
    sorter: {},
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  // counters
  const { fetchedData: deptFetched = {} } = useFetch(
    `${FETCH_ALL_DEPT_API}/active`,
    state
  );
  const deptList = deptFetched?.data || [];

  // fetch all counters
  const { fetchedData: countersFetched = {} } = useFetch(
    FETCH_ALL_COUNTER_API,
    state
  );
  const counters = countersFetched?.data || [];

  // build filtered counters (only where counter.dept is in active dept names)
  const counterData = useMemo(() => {
    if (!Array.isArray(deptList) || deptList.length === 0) return counters; // fallback before dept data arrives
    const activeDeptNames = new Set(
      deptList.map((d) => d?.name).filter(Boolean)
    );
    return counters.filter((c) => c?.dept && activeDeptNames.has(c.dept));
  }, [counters, deptList]);

  // colors
  const {
    fetchedData: colorResp,
    callReFetchColor,
    loadingColor,
  } = useFetch(`${FETCH_ALL_COLOR_API}`, state);
  const ColorData = colorResp?.data ?? [];

  // generate robust noForm at mount
  useEffect(() => {
    const ts = new Date();
    const stamp = ts.toISOString().replace(/\D/g, "").slice(0, 14); // YYYYMMDDHHMMSS
    
    // Special case for ECOMMRI: sender is always GP, destination is ECOMMRI
    const senderCode = userData?.counterCode === 'ECOMMRI' ? 'GP' : userData?.counterCode;
    
    form.setFieldsValue({
      noForm: `GD${stamp}-${uuidv4().slice(0, 8).toUpperCase()}`,
      sender: senderCode,
    });
  }, [form, userData?.counterCode]);

  // Debug useEffect to monitor state changes
  useEffect(() => {
    console.log("=== State Change Debug ===");
    console.log("editingItemIndex changed to:", editingItemIndex);
    console.log("isTempEditMode changed to:", isTempEditMode);
    console.log("isModalVisible changed to:", isModalVisible);
  }, [editingItemIndex, isTempEditMode, isModalVisible]);

  // derived totals
  const totals = useMemo(() => {
    const totalQuantity = items.reduce(
      (s, it) => s + toNumber(it.quantity || 0),
      0
    );
    const totalCost = items.reduce(
      (s, it) => s + toNumber(it.price || 0) * toNumber(it.quantity || 0),
      0
    );
    return { totalQuantity, totalCost };
  }, [items]);

  // detect duplicates based on noSJ + barcode combination (same as backend unique constraint)
  const duplicateItems = useMemo(() => {
    const duplicateMap = new Map();
    const duplicates = new Set();
    
    items.forEach((item, index) => {
      // Create the same key that would cause duplicate key error in MongoDB
      const key = `${item.noSJ || 'no-sj'}-${item.barcode}`;
      
      if (duplicateMap.has(key)) {
        // Mark both the original and current item as duplicates
        duplicates.add(duplicateMap.get(key));
        duplicates.add(index);
      } else {
        duplicateMap.set(key, index);
      }
    });
    
    return duplicates;
  }, [items]);

  // Handle article selection from StockCreationForm (reserved for your internal use)
  const handleArticleSelect = (article) => {
    setSelectedArticle(article);
  };

  // Validate modal item fields (basic guard in JS; you should also add AntD rules in StockCreationForm)
  const validateModalValues = (values) => {
    if (!values.article) throw new Error("Article is required.");
    if (!values.color) throw new Error("Color is required.");
    if (!values.size) throw new Error("Size is required.");

    const sizes = parseSizes(values.size);
    if (sizes.length === 0) {
      throw new Error("Use '07', '12 15', '12-15', '1215', or '4244'.");
    }

    const perPack = Number(String(values.quantity ?? "").trim());
    if (!Number.isInteger(perPack) || perPack <= 0) {
      throw new Error("Quantity must be a positive integer.");
    }

    const price = Number(
      String(values.price ?? "").replace(/(?<=\d)[,.\s](?=\d{3}\b)/g, "")
    );
    const hi = Number(
      String(values.harga_internal ?? "").replace(
        /(?<=\d)[,.\s](?=\d{3}\b)/g,
        ""
      )
    );
    if (!Number.isFinite(price) || price < 0) throw new Error("Invalid price.");
    if (!Number.isFinite(hi) || hi < 0)
      throw new Error("Invalid internal price.");

    if (!values.barcode || String(values.barcode).length < 2) {
      throw new Error("Barcode base is invalid.");
    }

    // assort: allow compact digits like "111" for 3 sizes
    const isRange = sizes.length > 1;
    const rawAssort = values.assort;
    let aList = [];

    if (Array.isArray(rawAssort)) {
      aList = rawAssort;
    } else {
      const str = String(rawAssort ?? "").trim();
      if (/^\d+$/.test(str) && isRange && str.length === sizes.length) {
        aList = str.split(""); // "111" -> ["1","1","1"]
      } else {
        aList = str.split(/[,\s]+/).filter(Boolean);
      }
    }

    if (!aList.length) throw new Error("Assort required.");
    if (aList.some((x) => !/^\d+$/.test(String(x)))) {
      throw new Error("Assort must be non-negative integers.");
    }
    if (isRange && aList.length !== sizes.length) {
      throw new Error(
        `Assort length must match range count (${sizes.length}).`
      );
    }

    return true;
  };
  
  // Add or Edit Item from modal
  const handleAddOrEditItem = async () => {
    try {
      const values = await modalForm.validateFields();

      const sizes = parseSizes(values.size);
      const isRange = sizes.length > 1;

      // Build rows
      let rows = [];

      if (isRange) {
        const aList = normalizeAssortList(values.assort, sizes.length);
        const perPack = Number(values.quantity || 0);

        rows = sizes
          .map((s2, i) => {
            const a = Number(aList[i] ?? 1); // <-- per-size assort
            const qty = a * perPack;

            return {
              id: uuidv4(),
              article: values.article,
              brand: values.brand, // Include brand for SKU generation
              color: values.color,
              size: s2,
              assort: String(a), // <-- store a single value
              inputQuantity: perPack,
              quantity: qty,
              price: Number(values.price) || 0,
              harga_internal: Number(values.harga_internal) || 0,
              barcode: `${values.barcode.slice(0, -2)}${s2}`,
            };
          })
          .filter((r) => Number(r.assort) > 0); // drop zeros
      } else {
        const perPack = Number(values.quantity || 0);
        const a = Number(values.assort ?? 1);
        rows = [
          {
            id: uuidv4(),
            article: values.article,
            brand: values.brand, // Include brand for SKU generation
            color: values.color,
            size: String(values.size).padStart(2, "0"),
            assort: String(a),
            inputQuantity: perPack,
            quantity: a * perPack,
            price: Number(values.price) || 0,
            harga_internal: Number(values.harga_internal) || 0,
            barcode: values.barcode,
          },
        ];
      }

      // Debug logging
      console.log("=== handleAddOrEditItem Debug ===");
      console.log("editingItemIndex:", editingItemIndex);
      console.log("isTempEditMode:", isTempEditMode);
      console.log("Should replace item:", editingItemIndex !== null && isTempEditMode);
      console.log("New rows to add:", rows);
      console.log("Current items count:", items.length);

      setItems((prev) => {
        // If we're in edit mode and not adding a new item, replace the existing item
        if (editingItemIndex !== null && isTempEditMode) {
          console.log("REPLACING item at index:", editingItemIndex);
          const copy = [...prev];
          copy.splice(editingItemIndex, 1, ...rows);
          return copy;
        } else {
          console.log("ADDING new items to list");
          return [...prev, ...rows];
        }
      });

      modalForm.resetFields();
      setIsModalVisible(false);
      setEditingItemIndex(null);
      setIsTempEditMode(false);
    } catch (err) {
      console.error("Validation Failed:", err);
      message.error(err?.message || "Validation Failed");
    }
  };
  
  const handleEditItem = (index) => {
    console.log("=== handleEditItem Debug ===");
    console.log("Editing item at index:", index);
    console.log("Item details:", items[index]);
    
    const it = items[index];
    setEditingItemIndex(index);
    modalForm.setFieldsValue({
      ...it,
      // ensure the modal quantity shows per-pack input
      quantity: it.inputQuantity,
      isRange: false,
      // for edit we keep size as currently stored (string "07")
      size: it.size,
      assort: it.assort,
      barcode: it.barcode, // already full barcode; if your modal expects base barcode, you may need to map back
    });
    setIsModalVisible(true);
    setIsTempEditMode(true);
    
    console.log("Set editingItemIndex to:", index);
    console.log("Set isTempEditMode to: true");
  };

  const handleDeleteItem = (index) => {
    const it = items[index];
    Modal.confirm({
      title: "Delete this item?",
      content: `Article: ${it.article} | Color: ${it.color} | Size: ${it.size}`,
      okText: "Delete",
      okType: "danger",
      onOk: () => {
        setItems((prev) => prev.filter((_, i) => i !== index));
        message.success("Item deleted.");
      },
    });
  };

  const handleFormSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();

      if (!items.length) {
        message.error("You must add at least one item!");
        return;
      }

      if (duplicateItems.size > 0) {
        message.error("Please resolve duplicate items before submitting!");
        return;
      }

      // prevent same sender & destination (except for ECOMMRI special case)
      const sender = values.sender;
      const dest = values.destinationCode;
      if (!dest) {
        message.error("Please select a destination counter.");
        return;
      }
      
      // For ECOMMRI users, sender is GP and destination is ECOMMRI, so they're different
      // For other users, check if sender and destination are the same
      if (userData?.counterCode !== 'ECOMMRI' && sender === dest) {
        message.error("Sender and destination must be different.");
        return;
      }

      const shippingData = {
        noForm: values.noForm,
        noSJ: values.noSJ,
        sender,
        destination: dest, // keep a single field; your backend can map it
        keterangan: values.keterangan,
        totalCost: totals.totalCost,
        totalQuantity: totals.totalQuantity,
        consignmentType,
        status: "in delivery",
        tanggal_kirim: toISO(values.tanggal_kirim),
        tanggal_terima_eta: toISO(values.tanggal_terima_eta),
      };

      const stockData = buildStockData(values, items, consignmentType);
      if (!stockData.length) {
        message.error("No valid stock rows to submit. Check items.");
        return;
      }

      // Use transactional API to create both shipping and stock atomically
      await createShippingWithStock(shippingData, stockData);

      message.success("Shipping details submitted successfully!");
      form.resetFields();
      setItems([]);
      // regenerate noForm after success
      const ts = new Date();
      const stamp = ts.toISOString().replace(/\D/g, "").slice(0, 14);
      
      // Special case for ECOMMRI: sender is always GP, destination is ECOMMRI
      const senderCode = userData?.counterCode === 'ECOMMRI' ? 'GP' : userData?.counterCode;
      
      form.setFieldsValue({
        noForm: `GD${stamp}-${uuidv4().slice(0, 8).toUpperCase()}`,
        sender: senderCode,
        destinationCode: userData?.counterCode === 'ECOMMRI' ? 'ECOMMRI' : undefined,
      });
    } catch (error) {
      console.error("Submit error:", error);
      
      // Handle specific error types
      if (error?.response?.status === 409) {
        // Duplicate stock entry error
        message.error(error?.response?.data?.message || "Duplicate stock entry found. Please check your data.");
      } else if (error?.response?.status === 400) {
        // Validation error
        message.error(error?.response?.data?.message || "Invalid data provided. Please check your inputs.");
      } else {
        // Generic error
        message.error(error?.response?.data?.message || error?.message || "Failed to submit shipping details.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: "Article", dataIndex: "article", key: "article" },
    { title: "Warna", dataIndex: "color", key: "color" },
    { title: "Size", dataIndex: "size", key: "size" },
    { title: "Barcode", dataIndex: "barcode", key: "barcode" },
    { title: "Assort", dataIndex: "assort", key: "assort" },
    {
      title: "Qty",
      dataIndex: "inputQuantity",
      key: "inputQuantity",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Harga",
      dataIndex: "price",
      key: "price",
      render: (v) => (toNumber(v) || 0).toLocaleString(),
    },
    {
      title: "Status",
      key: "status",
      render: (_, __, index) => {
        if (duplicateItems.has(index)) {
          return (
            <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
              ⚠️ Duplicate
            </span>
          );
        }
        return <span style={{ color: '#52c41a' }}>✓ Valid</span>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, __, index) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button 
            type={editingItemIndex === index ? "primary" : "default"}
            onClick={() => handleEditItem(index)}
          >
            {editingItemIndex === index ? "Editing..." : "Edit"}
          </Button>
          <Button danger onClick={() => handleDeleteItem(index)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  // Select options for counters (exclude current and PRI)
  const destinationOptions = useMemo(() => {
    // Special case for ECOMMRI: destination is always ECOMMRI
    if (userData?.counterCode === 'ECOMMRI') {
      const ecommriCounter = (counterData || []).find(c => c.code === 'ECOMMRI');
      return ecommriCounter ? [{ label: ecommriCounter.name, value: ecommriCounter.code }] : [];
    }
    
    // Normal case: exclude current and PRI
    return (counterData || [])
      .filter((c) => c.code !== userData?.counterCode && c.code !== "PRI")
      .map((c) => ({ label: c.name, value: c.code }));
  }, [counterData, userData?.counterCode]);

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <style>
        {`
          .duplicate-row {
            background-color: #fff2f0 !important;
            border-left: 4px solid #ff4d4f !important;
          }
          .duplicate-row:hover {
            background-color: #ffe7e6 !important;
          }
          .duplicate-row td {
            color: #ff4d4f !important;
            font-weight: 500 !important;
          }
        `}
      </style>
      <h2>Create Shipping</h2>

      <Form.Item label="Consignment Type" required>
        <Radio.Group
          onChange={(e) => setConsignmentType(e.target.value)}
          value={consignmentType}
          style={{ marginBottom: 16 }}
          buttonStyle="solid"
        >
          <Radio.Button value="consignment">Consignment</Radio.Button>
          <Radio.Button value="putus">Beli Putus</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <div style={{ width: "80vw", maxWidth: 1200 }}>
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="No Form" name="noForm">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="No Surat Jalan"
                name="noSJ"
                rules={[
                  { required: true, message: "Please input Shipping Number!" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Kirim dari"
                name="sender"
                rules={[
                  { required: true, message: "Please select a Counter!" },
                ]}
                initialValue={userData?.counterCode === 'ECOMMRI' ? 'GP' : userData?.counterCode}
              >
                <Select
                  disabled
                  options={
                    userData?.counterCode === 'ECOMMRI' 
                      ? (counterData || [])
                          .filter((c) => c.code === 'GP')
                          .map((c) => ({ label: c.name, value: c.code }))
                      : (counterData || [])
                          .filter((c) => c.code === userData?.counterCode)
                          .map((c) => ({ label: c.name, value: c.code }))
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Kirim ke"
                name="destinationCode"
                rules={[
                  { required: true, message: "Please select a Counter!" },
                ]}
                initialValue={userData?.counterCode === 'ECOMMRI' ? 'ECOMMRI' : undefined}
              >
                <Select
                  showSearch={userData?.counterCode !== 'ECOMMRI'}
                  placeholder="Select a Counter"
                  options={destinationOptions}
                  disabled={userData?.counterCode === 'ECOMMRI'}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Keterangan" name="keterangan">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Tanggal Kirim"
                name="tanggal_kirim"
                rules={[
                  { required: true, message: "Please select Tanggal Kirim!" },
                ]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Tanggal Terima ETA"
                name="tanggal_terima_eta"
                rules={[
                  {
                    required: true,
                    message: "Please select Tanggal Terima ETA!",
                  },
                ]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Total Value">
                <Input
                  prefix="Rp."
                  value={totals.totalCost.toLocaleString()}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Total Pasang">
                <Input value={totals.totalQuantity} disabled />
              </Form.Item>
            </Col>
          </Row>

          {editingItemIndex !== null && (
            <div style={{ 
              marginBottom: 16, 
              padding: 12, 
              backgroundColor: '#f0f8ff', 
              border: '1px solid #91d5ff', 
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span style={{ fontWeight: 500, color: '#1890ff' }}>
                📝 Editing Item: {items[editingItemIndex]?.article} - {items[editingItemIndex]?.color} - Size {items[editingItemIndex]?.size}
              </span>
            </div>
          )}

          {duplicateItems.size > 0 && (
            <div style={{ 
              marginBottom: 16, 
              padding: 12, 
              backgroundColor: '#fff2f0', 
              border: '1px solid #ffccc7', 
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span style={{ color: '#ff4d4f', fontSize: '16px' }}>⚠️</span>
              <span style={{ fontWeight: 500, color: '#ff4d4f' }}>
                Duplicate items detected! Please remove or modify the highlighted rows before submitting.
                {duplicateItems.size > 1 && ` (${duplicateItems.size} items have duplicates)`}
              </span>
            </div>
          )}

          <Table
            columns={columns}
            dataSource={items}
            rowKey={(record) => record.id}
            pagination={false}
            rowClassName={(record, index) => {
              if (duplicateItems.has(index)) {
                return 'duplicate-row';
              }
              return '';
            }}
            footer={() => (
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 24 }}
              >
                <div>
                  <b>Total Pasang:</b> {totals.totalQuantity}
                </div>
                <div>
                  <b>Total Value:</b> Rp. {totals.totalCost.toLocaleString()}
                </div>
              </div>
            )}
          />

          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <Tooltip title={editingItemIndex !== null ? "Add a new item while keeping current edit context" : "Add a new item to the shipping"}>
              <Button
                type="primary"
                onClick={() => {
                  console.log("=== Add Item Button Click Debug ===");
                  console.log("Current editingItemIndex:", editingItemIndex);
                  console.log("Current isTempEditMode:", isTempEditMode);
                  
                  // If we're editing an item, allow adding new items
                  if (editingItemIndex !== null) {
                    console.log("Adding new item while editing - setting isAddingNewItem = true");
                    // Add a new item while keeping the current edit context
                    setIsAddingNewItem(true); // This ensures we're adding, not editing
                    modalForm.resetFields();
                  } else {
                    console.log("Normal add item flow - clearing all edit state");
                    // Normal add item flow
                    setEditingItemIndex(null);
                    setIsTempEditMode(false);
                    setIsAddingNewItem(false);
                    modalForm.resetFields();
                  }
                  setIsModalVisible(true);
                }}
              >
                {editingItemIndex !== null ? "Add New Item" : "Add Item"}
              </Button>
            </Tooltip>
            {editingItemIndex !== null && (
              <Tooltip title="Cancel editing and return to normal mode">
                <Button
                  onClick={() => {
                    console.log("=== Cancel Edit Button Debug ===");
                    console.log("Canceling edit, clearing edit state");
                    // Cancel editing and return to normal mode
                    setEditingItemIndex(null);
                    setIsTempEditMode(false);
                    modalForm.resetFields();
                  }}
                >
                  Cancel Edit
                </Button>
              </Tooltip>
            )}
            <Tooltip title={duplicateItems.size > 0 ? "Please resolve duplicate items before submitting" : "Submit the shipping with all items"}>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={submitting}
                disabled={duplicateItems.size > 0}
              >
                Submit
              </Button>
            </Tooltip>
          </div>
        </Form>

        <Modal
          title={editingItemIndex === null ? "Add New Item" : "Edit Item"}
          open={isModalVisible}
          onOk={handleAddOrEditItem}
          onCancel={() => {
            console.log("=== Modal Cancel Debug ===");
            console.log("Closing modal, clearing edit state");
            setIsModalVisible(false);
            setEditingItemIndex(null);
            setIsTempEditMode(false);
            modalForm.resetFields();
          }}
          okText={editingItemIndex === null ? "Add" : "Update"}
          confirmLoading={modalSubmitting}
          destroyOnClose
        >
          <StockCreationForm
            modalForm={modalForm}
            ColorData={ColorData}
            isTempEditMode={isTempEditMode}
            onArticleSelect={handleArticleSelect}
            userData={userData}
          />
        </Modal>
      </div>
    </div>
  );
};

export default ShippingCreationPage;
